/**
 * 系统管理费用配置域的唯一 HTTP / DTO 入口。
 *
 * 该 Facade 不读取 DOM、不保存页面状态；它只把既有管理端接口归一为
 * Controller 可消费的数据和错误形态，保留旧接口与请求体不变。
 */
export function createAdminCostCalculationFacade({ request }) {
  async function loadPageData() {
    const [runtimeResponse, usageResponse, limitsResponse] = await Promise.all([
      request('/api/admin/runtime-settings'),
      request('/api/admin/usage?page=1&size=100'),
      request('/api/admin/usage-limits/users?page=1&size=20'),
    ]);
    const runtimePayload = await readPayload(runtimeResponse);
    if (!runtimeResponse.ok) throw new Error(runtimePayload?.error || '运行时配置读取失败');
    return {
      runtimeSettings: Array.isArray(runtimePayload?.settings) ? runtimePayload.settings : [],
      usage: usageResponse.ok ? await readPayload(usageResponse) : { systemTotal: null, users: [], total: 0 },
      usageLimits: limitsResponse.ok ? await readPayload(limitsResponse) : { users: [], total: 0, page: 1, size: 20 },
    };
  }

  async function listUsageLimits({ page = 1, size = 20, search = '' } = {}) {
    const params = new URLSearchParams({ page, size });
    if (search) params.set('search', search);
    const response = await request(`/api/admin/usage-limits/users?${params}`);
    const payload = await readPayload(response);
    if (!response.ok) throw new Error(payload?.error || payload?.message || '加载用户限额失败');
    return payload;
  }

  async function saveUsageLimits({ mode, userIds, limits }) {
    const isSingleUser = mode === 'single' && userIds.length === 1;
    const response = await request(
      isSingleUser ? `/api/admin/usage-limits/users/${userIds[0]}` : '/api/admin/usage-limits/batch',
      {
        method: isSingleUser ? 'PUT' : 'POST',
        body: JSON.stringify({
          ...(mode === 'batch' ? { userIds } : {}),
          limits,
        }),
      },
    );
    const payload = await readPayload(response);
    if (!response.ok) throw new Error(payload?.error || payload?.message || '保存限额失败');
    return payload;
  }

  async function saveRuntimeSettings(settings) {
    const response = await request('/api/admin/runtime-settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
    const payload = await readPayload(response);
    if (!response.ok) throw new Error(payload?.error || payload?.message || '保存失败');
    return payload;
  }

  return {
    listUsageLimits,
    loadPageData,
    saveRuntimeSettings,
    saveUsageLimits,
  };
}

async function readPayload(response) {
  return response.json().catch(() => ({}));
}
